import {useMutation, useQuery} from "@apollo/client";
import toast from "react-hot-toast";
import {Link} from "react-router";

import {GQL_USER_CONTEXT} from "@/lib/queries/accounts.ts";
import {
  CalculatedBillingDataResponse,
  GQL_CALCULATE_BILLING_DATA,
  GQL_PURCHASE_ADDONS,
  PurchaseAddonsResponse,
} from "@/lib/queries/billing.ts";
import {BillingPeriod, CalculatedBillingData} from "@/lib/types/billing.ts";
import {CheckTypeDisplay, MonitoringServiceType} from "@/lib/types/checks.ts";
import {QueryError, ResponseStatus} from "@/lib/types/common.ts";
import {AddOnBox} from "@/pages/billing/v1/instantCheckout/AddOnBox.tsx";
import {InstantPopUp} from "@/pages/billing/v1/instantCheckout/InstantPopUp.tsx";

interface UpgradePlanPopupProps {
  show: boolean;
  onComplete: () => void;
  service: MonitoringServiceType;
  limitField: string;
  amount: number;
  period: BillingPeriod;
  maxLimit: number;
  accountLimit: number;
}

export const PopupAddOns = ({
  show,
  service,
  amount,
  period,
  limitField,
  onComplete,
  maxLimit,
  accountLimit,
}: UpgradePlanPopupProps) => {
  const [purchaseAddOns, {loading: purchaseLoading}] = useMutation<PurchaseAddonsResponse>(GQL_PURCHASE_ADDONS, {
    refetchQueries: [GQL_USER_CONTEXT],
  });
  const {data, loading, refetch, variables} = useQuery<CalculatedBillingDataResponse>(GQL_CALCULATE_BILLING_DATA, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    variables: {
      billingPeriod: period,
      addedItems: [
        {
          limit_field: limitField,
          amount: amount,
        },
      ],
      skipApplicabilityCheck: true,
    },
  });
  if (loading) {
    return null;
  }

  const billingResult = data?.calculateBillingData;

  if ((billingResult as QueryError)?.status === ResponseStatus.ERROR) {
    console.error("Error calculating billing data:", (billingResult as QueryError).message);
    return null;
  }

  const billingData = billingResult as CalculatedBillingData;
  const variablesAmount = variables?.addedItems?.[0]?.amount || amount;
  return (
    <InstantPopUp
      show={show}
      loading={loading || purchaseLoading}
      onConfirm={async () => {
        const r = await purchaseAddOns({
          variables: {
            addedValues: {
              limit_field: limitField,
              amount: variablesAmount,
            },
          },
        });
        if (r.data?.purchaseAddOns.success) {
          toast.success(`Extra ${CheckTypeDisplay[service]} purchased successfully!`);
        }
        onComplete();
      }}
      title={`Upgrade to add ${CheckTypeDisplay[service]} Checks`}
      alertCallout={`You have reached the limit of ${accountLimit} ${CheckTypeDisplay[service]} checks on your current plan `}
      alertChildren={
        <span>
          Add {CheckTypeDisplay[service]} checks to your account now.&nbsp;
          <Link to="/billing?action=manage-subscription">View all add-ons</Link>
        </span>
      }
      onCancel={onComplete}
      btnTitle="Upgrade Subscription"
    >
      <AddOnBox
        amount={variablesAmount}
        loading={loading || purchaseLoading}
        price={billingData.added_cost_full}
        onAdd={() => {
          return refetch({
            billingPeriod: period,
            addedItems: [
              {
                limit_field: limitField,
                amount: variablesAmount + amount,
              },
            ],
            skipApplicabilityCheck: true,
          });
        }}
        onRemove={() => {
          return refetch({
            billingPeriod: period,
            addedItems: [
              {
                limit_field: limitField,
                amount: variablesAmount - amount,
              },
            ],
            skipApplicabilityCheck: true,
          });
        }}
        canAdd={variablesAmount < maxLimit}
        canRemove={variablesAmount > amount}
      />
    </InstantPopUp>
  );
};
